import { Capacitor } from "@capacitor/core";
import { usePaneSide } from "feat/layout/panes/pane-context";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useBottomInset } from "state/windowState/window.store";
import { useNavBarHeight } from "./navBar.store";
import { TAB_BAR_HIDDEN_PATHS_REGEX } from "./tab-utils";
import { resolveTabForPath } from "./tabs";

// The native tab bar is disabled: nativeTabbarReady stays false, so the web NavBar
// renders instead. The exports below stay in place so consumers that read them
// (bottom-safe-area padding, etc.) keep working.
//
// These were `signal(false)` but nothing in the repo ever assigned to either of them,
// so they were constants wearing a signal costume — no subscription ever fired. Plain
// consts are exactly equivalent and let the dead branches below stay visible.
export const nativeTabbarVisible = false;
export const nativeTabbarReady = false;

export const NativeTabbarBridge = (_props: { forceHidden?: boolean }) => null;

const matchesAnyPattern = (path: string, patterns: RegExp[]) => patterns.some((pattern) => pattern.test(path));

export const resolveSelectedTabId = (path: string, fallback: string) => {
  return resolveTabForPath(path)?.id ?? fallback;
};

export const isNativeTabbarHiddenPath = (path: string) => {
  return matchesAnyPattern(path, TAB_BAR_HIDDEN_PATHS_REGEX);
};

/**
 * Was `getTabBarBottomSafeOffset()`, a plain function reading `windowSizes.value`.
 *
 * Because signals tracked any `.value` read that happened during a render, calling it
 * from a component body silently subscribed that component to `windowSizes` — an
 * invisible subscription at 8 call sites. `useWindowStore.getState()` would have
 * compiled fine and left every one of them with stale safe-area padding after a
 * rotation or keyboard event, with no type error to catch it. Hence a hook.
 *
 * The bar's height is read from the store, not measured off `navBarRef` here. The height
 * animates (VerticalExpander, 200ms) and the bar collapses entirely on the
 * TAB_BAR_HIDDEN_PATHS routes, so a render-time `offsetHeight` read gave whatever the
 * height happened to be during that one render and never corrected — see the ResizeObserver
 * in NavBar for the failure it caused. The store field carries the same value with a
 * subscription attached, so every consumer re-lays-out as the bar moves.
 */
export function useTabBarBottomSafeOffset(): number {
  const bottomInset = useBottomInset();
  const navBarHeight = useNavBarHeight();
  const paneSide = usePaneSide();

  // A web pane already ends above the tab bar. A native split column is a full-height web view.
  if (paneSide !== null && !isIosNativeNavigation()) return 0;

  if (!Capacitor.isNativePlatform()) {
    // Web: the bar's own height (0 when hidden).
    return navBarHeight;
  }

  if (nativeTabbarVisible) {
    // Native tabbar visible (iOS): return just bottom safe area inset
    return bottomInset;
  }

  // Android (web NavBar) or hidden tabbar:
  if (navBarHeight > 0) {
    // Web NavBar visible (Android): its height already includes the bottom inset
    return navBarHeight;
  }

  // Tabbar hidden but still need safe area inset on native
  return bottomInset;
}

/**
 * CSS `bottom` value that clears the tab bar's safe area, as a string (for
 * fixed-position elements such as FABs and footers).
 *
 * eventr-native's iOS fork writes no `--native-navigation-inset-*` CSS vars, that is
 * Android-only (see ViewSpecFragment.kt), so on iOS the var always falls through to
 * `env(safe-area-inset-bottom)`, which WebKit derives natively from the per-view
 * webview's own `safeAreaInsets`. Each tab's webview sits inside UITabBarController,
 * so UIKit auto-extends that safe area to include the tab bar height, and `env()`
 * already reports the correct tab-bar-inclusive inset with no plugin code needed.
 * Same pattern as `NavigationView.tsx`'s `footerDeadZone`.
 */
export function useBottomSafeAreaOffset(extraPx = 0): string {
  // Called unconditionally — the early return below must not skip a hook.
  const tabBarOffset = useTabBarBottomSafeOffset();

  // Home's scroll-away publishes the bar's share of its travel gone as
  // --home-tabbar-gone-ratio, which shrinks the bar's inset in step with it. 0 elsewhere.
  const barGone = "var(--home-tabbar-gone-ratio, 0)";

  if (isIosNativeNavigation() && !nativeTabbarVisible) {
    const inset = "var(--native-navigation-inset-bottom, env(safe-area-inset-bottom, 0px))";
    return `calc(${inset} * (1 - ${barGone}) + ${extraPx}px)`;
  }
  return `calc(${tabBarOffset}px * (1 - ${barGone}) + ${extraPx}px)`;
}
